"use client";

import { Button } from "@heroui/react";
import { AptosSignInOutput, useWallet, groupAndSortWallets, WalletReadyState } from "@aptos-labs/wallet-adapter-react";
import { useAuth } from "@/contexts/auth-context";
import { useConnection } from "@/contexts/connection-context";
import { useState, useEffect } from "react";
import { AuthService } from "@/services/auth.service";
import { useQueryClient } from "@tanstack/react-query";
import { PetraDownloadModal } from "./PetraDownloadModal";
import { toast } from "sonner";
import { getErrorMessage } from "@/helpers/getErrorMessage";

interface WalletAuthButtonProps {
  variant?: "default" | "white";
}

export function WalletAuthButton({ variant = "default" }: WalletAuthButtonProps) {
  const { account, connected, disconnect, signIn, wallets = [], notDetectedWallets = [] } = useWallet();
  const { setTokens, clearTokens } = useAuth();
  const { connectWallet: setConnected, disconnectWallet: setDisconnected, isAuthenticated, setIsAuthenticated } = useConnection();
  const [isLoading, setIsLoading] = useState(false);
  const [sessionAddress, setSessionAddress] = useState<string | null>(null);
  const [showPetraModal, setShowPetraModal] = useState(false);
  const queryClient = useQueryClient();

  // Group wallets for mobile support
  const { availableWallets, installableWallets } = groupAndSortWallets([
    ...wallets, 
    ...notDetectedWallets
  ]);

  // Check if we're on mobile
  const isMobile = typeof window !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Find Petra wallet (available or installable)
  const petraWallet = [...availableWallets, ...installableWallets].find(wallet => wallet.name === "Petra");

  // Get wallet address from session on mount
  useEffect(() => {
    const getSessionAddress = async () => {
      if (isAuthenticated && !connected) {
        const authStatus = await AuthService.checkAuthStatus();
        if (authStatus.isAuthenticated && authStatus.verifyOutput?.user.address) {
          setSessionAddress(authStatus.verifyOutput.user.address);
          setTokens(authStatus.verifyOutput.token);
        }
      }
    };
    getSessionAddress();
  }, [isAuthenticated, connected, setTokens]);

  const handleMobileWalletConnection = async () => {
    try {
      console.log("Mobile device detected, attempting Petra wallet connection...");
      
      // For mobile, we need to use a different approach
      // First, try to connect directly using the wallet adapter
      try {
        console.log("Attempting direct mobile connection...");
        const { input } = await AuthService.getSignInInput();
        const output = await signIn({
          walletName: "Petra",
          input: input,
        });

        if (output && output.signature) {
          console.log("Mobile connection successful!");
          await handleSignInResponse(output);
          return;
        }
      } catch (directError) {
        console.log("Direct mobile connection failed:", directError);
      }

      // If direct connection fails, try deep linking approach
      if (petraWallet && "deeplinkProvider" in petraWallet && petraWallet.deeplinkProvider) {
        console.log("Trying deep link approach:", petraWallet.deeplinkProvider);
        
        // Create a proper deep link URL with our domain
        const currentUrl = encodeURIComponent(window.location.href);
        const deepLinkUrl = `${petraWallet.deeplinkProvider}${currentUrl}`;
        
        console.log("Redirecting to deep link:", deepLinkUrl);
        
        // Show user what's happening
        toast.info("Opening Petra Wallet...", {
          duration: 3000,
          dismissible: true,
          style: {
            background: "#e0f2fe",
            color: "#0284c7",
            border: "1px solid #7dd3fc"
          }
        });
        
        // Redirect to deep link
        window.location.href = deepLinkUrl;
      } else {
        // Fallback: Show instructions for manual connection
        console.log("Petra wallet not found or no deep link support, showing instructions");
        
        toast.info("Please open Petra Wallet app and scan this QR code or manually connect to: " + window.location.hostname, {
          duration: 8000,
          dismissible: true,
          style: {
            background: "#e0f2fe",
            color: "#0284c7",
            border: "1px solid #7dd3fc"
          }
        });
        
        // Show the install modal as fallback
        setShowPetraModal(true);
      }
      
    } catch (error) {
      console.error("Mobile wallet connection error:", error);
      toast.error("Failed to connect with Petra Wallet on mobile", {
        duration: 5000,
        dismissible: true,
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectAndSign = async () => {
    try {
      setIsLoading(true);
      console.log("Starting wallet connection and authentication process...");
      console.log("Current connection state:", { connected, isAuthenticated });

      if (isMobile) {
        // Mobile wallet detection and connection
        console.log("Mobile device detected, using mobile wallet flow");
        await handleMobileWalletConnection();
        return;
      }

      // Desktop wallet detection - check if Petra is available
      const isPetraReady = petraWallet && petraWallet.readyState === WalletReadyState.Installed;
      
      if (!isPetraReady) {
        console.log("Petra wallet not installed, showing download modal");
        setShowPetraModal(true);
        return;
      }

      console.log("Petra wallet detected, proceeding with connection...");

      // Check if wallet is already connected
      if (connected) {
        console.log("Wallet already connected, proceeding with authentication only...");
        // If wallet is already connected, just do authentication
        const { input: input } = await AuthService.getSignInInput();
        const output = await signIn({
          walletName: "Petra",
          input: input,
        });

        if (output && output.signature) {
          await handleSignInResponse(output);
          console.log("Authentication completed for already connected wallet");
        } else {
          throw new Error("No signature received from already connected wallet");
        }
        return;
      }

      const { input: input } = await AuthService.getSignInInput();

      const output = await signIn({
        walletName: "Petra",
        input: input,
      });

      if (output && output.signature) {
        await handleSignInResponse(output);
      } else {
        throw new Error("No signature received from wallet");
      }
    } catch (error) {
      console.error("Failed to connect and authenticate:", error);

      // Check if it's a network error or auth error
      const isNetworkError =
        error instanceof Error &&
        (error.message.includes("fetch") || error.message.includes("network") || error.message.includes("timeout") || error.message.includes("ECONNREFUSED"));

      const isAuthError =
        error instanceof Error &&
        (error.message.includes("unauthorized") ||
          error.message.includes("invalid") ||
          error.message.includes("signature") ||
          error.message.includes("authentication") ||
          error.message.includes("500") ||
          error.message.includes("Internal Server Error"));

      // Show user-friendly error message
      if (isNetworkError) {
        console.warn("Network error during authentication - user can retry");
        toast.error("Network error. Please check your connection and try again.", {
          duration: 5000,
          dismissible: true,
          style: {
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fca5a5"
          }
        });
      } else if (isAuthError) {
        console.warn("Authentication error (possibly 500 server error) - user needs to retry");
        toast.error(getErrorMessage(error, "Authentication failed. Please try again."), {
          duration: 5000,
          dismissible: true,
          style: {
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fca5a5"
          }
        });
      } else {
        console.error("Unknown error during authentication:", error);
        toast.error(getErrorMessage(error, "Failed to connect wallet. Please try again."), {
          duration: 5000,
          dismissible: true,
          style: {
            background: "#fee2e2",
            color: "#991b1b",
            border: "1px solid #fca5a5"
          }
        });
      }

      setIsAuthenticated(false);
      // Only disconnect if wallet was already connected and it's not a retry-able error
      if (connected && !isNetworkError && !isAuthError) {
        disconnect();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignInResponse = async (output: AptosSignInOutput) => {
    try {
      const response = await AuthService.siwaAuthenticate({
        input: output.input,
        signature: output.signature.bcsToHex().toString(),
        publicKey: output.account.publicKey.bcsToHex().toString(),
        type: output.type,
        version: "2",
      });

      if (response.accessToken) {
        setTokens(response.accessToken);
        console.log("Access token stored successfully");
      }

      const signedAddress = output.account.address.toString();

      // Update authentication state
      setIsAuthenticated(true);
      console.log("Authentication state updated to true");

      // Update connection state
      setConnected(signedAddress);
      console.log("Connection state updated");

      // Store authentication state
      localStorage.setItem(`siwa_auth_${signedAddress}`, "true");
      console.log("Authentication state stored in localStorage for address:", signedAddress);
    } catch (error) {
      console.error("Authentication failed with details:", {
        error: error,
        errorMessage: error instanceof Error ? error.message : "Unknown error",
        errorStack: error instanceof Error ? error.stack : undefined,
        output: {
          hasInput: !!output.input,
          hasSignature: !!output.signature,
          hasAccount: !!output.account,
        },
      });
      toast.error(getErrorMessage(error, "Authentication failed. Please try again."), {
        duration: 5000,
        dismissible: true,
        style: {
          background: "#fee2e2",
          color: "#991b1b",
          border: "1px solid #fca5a5"
        }
      });
      setIsAuthenticated(false);
      // Don't disconnect wallet on auth failure - let user retry
      // Only throw error to be handled by parent
      throw error;
    }
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);

      // Sign out from backend to clear cookies and tokens
      try {
        await AuthService.signOut();
      } catch (error) {
        console.error("Sign out error:", error);
      }

      // Disconnect wallet
      disconnect();

      // Clear React Query cache
      queryClient.removeQueries({ queryKey: ["auth-session"] });
      queryClient.removeQueries({ queryKey: ["auth-user"] });
      queryClient.removeQueries({ queryKey: ["challenges"] });
      queryClient.removeQueries({ queryKey: ["challenges", "task-status"] });
      queryClient.removeQueries({ queryKey: ["challenges", "user"] });
      queryClient.removeQueries({ queryKey: ["challenges", "leaderboard"] });
      queryClient.removeQueries({ queryKey: ["social-pay-user"] });

      // Clear all auth state
      setIsAuthenticated(false);
      clearTokens();
      setDisconnected();

      if (connected) {
        disconnect();
      }
    } catch (error) {
      console.error("Failed to sign out:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Format address for display
  const formatAddress = (addr: string) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // If authenticated (from cookie validation), show authenticated state
  if (isAuthenticated) {
    // Determine which address to show
    const addressToShow = connected && account ? account.address.toString() : sessionAddress;

    return (
      <>
        <div className="space-y-3">
          <div className="text-center">
            <p className="text-green-400 text-sm">Authenticated</p>
            {addressToShow ? <p className="text-white font-mono text-sm">{formatAddress(addressToShow)}</p> : <p className="text-gray-400 text-xs">Session Active</p>}
          </div>
          <Button className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-2xl min-h-[48px] flex items-center justify-center" onPress={handleDisconnect} isLoading={isLoading}>
            Sign Out
          </Button>
        </div>

        <PetraDownloadModal isOpen={showPetraModal} onClose={() => setShowPetraModal(false)} />
      </>
    );
  }

  // Not authenticated - show connect button
  const buttonClass =
    variant === "white"
      ? "bg-white hover:bg-gray-100 text-black font-semibold px-8 py-4 rounded-full text-lg transition-colors flex items-center justify-center"
      : "w-full bg-[#60FF8A] hover:bg-[#60FF8A]/90 text-black font-semibold rounded-2xl min-h-[48px] flex items-center justify-center";

  return (
    <>
      <div className="wallet-auth-button-container">
        <Button className={buttonClass} onPress={handleConnectAndSign} isLoading={isLoading}>
          Connect Wallet
        </Button>
      </div>

      <PetraDownloadModal isOpen={showPetraModal} onClose={() => setShowPetraModal(false)} />
    </>
  );
}
